import React, { useState } from 'react';
import { X, CheckCircle2, DollarSign, Sparkles } from 'lucide-react';
import { LeadSource } from '../../types/lead';
interface NewLeadFormData {
  NOMBRE_NEGOCIO: string;
  DECISOR_NOMBRE: string;
  DECISOR_CARGO: string;
  TELEFONO_WHATSAPP: string;
  calle: string;
  numero: string;
  barrio: string;
  ciudad: string;
  TAG_GEO: string;
  LEAD_SOURCE: LeadSource;
  ENLACE_GMB: string;
  instagram: string;
  facebook: string;
  NOTA_CARENCIA: string;
  VERIFICADO: boolean;
  MICRO_ACUERDO_PAGADO: boolean;
  CUALIFICADO: boolean;
}
const emptyForm: NewLeadFormData = {
  NOMBRE_NEGOCIO: '',
  DECISOR_NOMBRE: '',
  DECISOR_CARGO: '',
  TELEFONO_WHATSAPP: '',
  calle: '',
  numero: '',
  barrio: '',
  ciudad: '',
  TAG_GEO: '',
  LEAD_SOURCE: 'Otro',
  ENLACE_GMB: '',
  instagram: '',
  facebook: '',
  NOTA_CARENCIA: '',
  VERIFICADO: true,
  MICRO_ACUERDO_PAGADO: false,
  CUALIFICADO: false
};
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: NewLeadFormData) => void;
}
export function NewLeadModal({
  isOpen,
  onClose,
  onCreate
}: Props) {
  const [form, setForm] = useState<NewLeadFormData>(emptyForm);
  if (!isOpen) return null;
  const handleChange = (field: keyof NewLeadFormData, value: any) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.NOMBRE_NEGOCIO.trim()) return;
    onCreate(form);
    setForm(emptyForm);
    onClose();
  };
  const handleClose = () => {
    setForm(emptyForm);
    onClose();
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-608">
      <div className="bg-growthly-surface border border-white/10 rounded-xl shadow-2xl max-w-[640px] w-full overflow-hidden flex flex-col max-h-[90vh]" data-id="element-609">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-growthly-panel" data-id="element-610">
          <div data-id="element-611">
            <h2 className="text-[18px] font-semibold text-white" data-id="element-612">Nuevo Lead</h2>
            <p className="text-[12px] text-growthly-muted mt-0.5" data-id="element-613">
              Rellena la información del contacto. Solo el nombre del negocio es
              obligatorio.
            </p>
          </div>
          <button onClick={handleClose} className="text-growthly-muted hover:text-white transition-colors" aria-label="Cerrar" data-id="element-614">
            <X className="w-5 h-5" data-id="element-615" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-616">
          <div className="p-6 space-y-6" data-id="element-617">
            {/* Negocio */}
            <section data-id="element-618">
              <h3 className="text-[13px] font-semibold text-white mb-3 uppercase tracking-wide" data-id="element-619">
                Negocio
              </h3>
              <div className="space-y-3" data-id="element-620">
                <Field label="Nombre del negocio *" value={form.NOMBRE_NEGOCIO} onChange={v => handleChange('NOMBRE_NEGOCIO', v)} required data-id="element-621" />
                <div className="grid grid-cols-2 gap-3" data-id="element-622">
                  <Select label="Fuente" value={form.LEAD_SOURCE} onChange={v => handleChange('LEAD_SOURCE', v)} options={['GMB', 'IG', 'FB', 'Directorio', 'Referral', 'Otro']} data-id="element-623" />
                  <Field label="Territorio / Zona" value={form.TAG_GEO} onChange={v => handleChange('TAG_GEO', v)} data-id="element-624" />
                </div>
                <Field label="Enlace Google My Business" value={form.ENLACE_GMB} onChange={v => handleChange('ENLACE_GMB', v)} type="url" data-id="element-625" />
              </div>
            </section>

            {/* Decisor */}
            <section data-id="element-626">
              <h3 className="text-[13px] font-semibold text-white mb-3 uppercase tracking-wide" data-id="element-627">
                Persona de Contacto
              </h3>
              <div className="space-y-3" data-id="element-628">
                <div className="grid grid-cols-2 gap-3" data-id="element-629">
                  <Field label="Nombre" value={form.DECISOR_NOMBRE} onChange={v => handleChange('DECISOR_NOMBRE', v)} data-id="element-630" />
                  <Field label="Cargo" value={form.DECISOR_CARGO} onChange={v => handleChange('DECISOR_CARGO', v)} data-id="element-631" />
                </div>
                <Field label="Teléfono / WhatsApp" value={form.TELEFONO_WHATSAPP} onChange={v => handleChange('TELEFONO_WHATSAPP', v)} type="tel" data-id="element-632" />
              </div>
            </section>

            {/* Dirección */}
            <section data-id="element-633">
              <h3 className="text-[13px] font-semibold text-white mb-3 uppercase tracking-wide" data-id="element-634">
                Dirección
              </h3>
              <div className="grid grid-cols-3 gap-3" data-id="element-635">
                <div className="col-span-2" data-id="element-636">
                  <Field label="Calle" value={form.calle} onChange={v => handleChange('calle', v)} data-id="element-637" />
                </div>
                <Field label="Número" value={form.numero} onChange={v => handleChange('numero', v)} data-id="element-638" />
                <Field label="Barrio" value={form.barrio} onChange={v => handleChange('barrio', v)} data-id="element-639" />
                <div className="col-span-2" data-id="element-640">
                  <Field label="Ciudad" value={form.ciudad} onChange={v => handleChange('ciudad', v)} data-id="element-641" />
                </div>
              </div>
            </section>

            {/* RRSS */}
            <section data-id="element-642">
              <h3 className="text-[13px] font-semibold text-white mb-3 uppercase tracking-wide" data-id="element-643">
                Redes Sociales
              </h3>
              <div className="grid grid-cols-2 gap-3" data-id="element-644">
                <Field label="Instagram" value={form.instagram} onChange={v => handleChange('instagram', v)} data-id="element-645" />
                <Field label="Facebook" value={form.facebook} onChange={v => handleChange('facebook', v)} data-id="element-646" />
              </div>
            </section>

            {/* Carencia */}
            <section data-id="element-647">
              <h3 className="text-[13px] font-semibold text-white mb-3 uppercase tracking-wide" data-id="element-648">
                Observaciones
              </h3>
              <div data-id="element-649">
                <label className="block text-[12px] font-medium text-growthly-muted mb-1" data-id="element-650">
                  Nota de carencia / oportunidad
                </label>
                <textarea value={form.NOTA_CARENCIA} onChange={e => handleChange('NOTA_CARENCIA', e.target.value)} rows={3} className="input-field resize-none" data-id="element-651" />
              </div>
            </section>

            {/* Estado del Lead — IMPACTA EN DASHBOARD */}
            <section data-id="element-652">
              <h3 className="text-[13px] font-semibold text-white mb-1 uppercase tracking-wide" data-id="element-653">
                Estado del Lead
              </h3>
              <p className="text-[11px] text-growthly-muted mb-3" data-id="element-654">
                Estas opciones determinan en qué KPI del Dashboard aparece este
                lead.
              </p>
              <div className="space-y-2" data-id="element-655">
                <ToggleRow icon={<CheckCircle2 className="w-4 h-4 text-growthly-green" data-id="element-657" />} title="Marcar como verificado" description="Aparecerá en 'Leads Verificados' (objetivo 800)" checked={form.VERIFICADO} onChange={v => handleChange('VERIFICADO', v)} data-id="element-656" />
                <ToggleRow icon={<DollarSign className="w-4 h-4 text-growthly-amber" data-id="element-659" />} title="Ha pagado el micro-acuerdo (50€)" description="Aparecerá en 'Micro-acuerdos' del Dashboard" checked={form.MICRO_ACUERDO_PAGADO} onChange={v => {
                handleChange('MICRO_ACUERDO_PAGADO', v);
                if (!v) handleChange('CUALIFICADO', false);
              }} data-id="element-658" />
                <ToggleRow icon={<Sparkles className="w-4 h-4 text-white" data-id="element-661" />} title="Lead cualificado" description="Aparecerá en 'Leads Cualificados' (objetivo 35). Requiere micro-acuerdo pagado." checked={form.CUALIFICADO} onChange={v => {
                handleChange('CUALIFICADO', v);
                if (v) handleChange('MICRO_ACUERDO_PAGADO', true);
              }} data-id="element-660" />
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/5 bg-growthly-panel flex justify-end space-x-3 sticky bottom-0" data-id="element-662">
            <button type="button" onClick={handleClose} className="btn-secondary" data-id="element-663">
              Cancelar
            </button>
            <button type="submit" disabled={!form.NOMBRE_NEGOCIO.trim()} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-664">
              Crear lead
            </button>
          </div>
        </form>
      </div>
    </div>;
}
interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}
function Field({
  label,
  value,
  onChange,
  type = 'text',
  required
}: FieldProps) {
  return <div data-id="element-665">
      <label className="block text-[12px] font-medium text-growthly-muted mb-1" data-id="element-666">
        {label}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} className="input-field" data-id="element-667" />
    </div>;
}
interface SelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}
function Select({
  label,
  value,
  onChange,
  options
}: SelectProps) {
  return <div data-id="element-668">
      <label className="block text-[12px] font-medium text-growthly-muted mb-1" data-id="element-669">
        {label}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)} className="input-field" data-id="element-670">
        {options.map(opt => <option key={opt} value={opt} data-id="element-671">
            {opt}
          </option>)}
      </select>
    </div>;
}
interface ToggleRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}
function ToggleRow({
  icon,
  title,
  description,
  checked,
  onChange
}: ToggleRowProps) {
  return <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${checked ? 'bg-white/[0.06] border-white/20' : 'bg-growthly-bg border-white/5 hover:border-white/10'}`} data-id="element-672">
      <div className="flex-shrink-0 w-8 h-8 rounded-md bg-growthly-panel flex items-center justify-center mt-0.5" data-id="element-673">
        {icon}
      </div>
      <div className="flex-1 min-w-0" data-id="element-674">
        <div className="text-[13px] font-medium text-white" data-id="element-675">{title}</div>
        <div className="text-[11px] text-growthly-muted mt-0.5" data-id="element-676">
          {description}
        </div>
      </div>
      <div className="flex-shrink-0 pt-1" data-id="element-677">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" data-id="element-678" />
        <div className={`w-9 h-5 rounded-full transition-colors relative ${checked ? 'bg-white' : 'bg-white/10'}`} data-id="element-679">
          <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform ${checked ? 'translate-x-[18px] bg-growthly-bg' : 'translate-x-0.5 bg-white'}`} data-id="element-680" />
        </div>
      </div>
    </label>;
}
export type { NewLeadFormData };